import React from 'react';
import { Bell, Clock, BookOpen, BookMarked, Mail, Calendar, Repeat } from 'lucide-react';

const NotificationSettings = ({ profileData, onUpdate, isEditing }) => {
  const notifications = profileData?.notifications || {};
  const reminderSettings = profileData?.reminderSettings || {};

  const handleNotificationChange = (key, value) => {
    onUpdate?.({ notifications: { ...notifications, [key]: value } });
  };

  const handleReminderChange = (key, value) => {
    onUpdate?.({ reminderSettings: { ...reminderSettings, [key]: value } });
  };

  const inputClass = (disabled) =>
    `block w-full px-3 py-2 border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm ${
      disabled ? 'bg-muted border-border cursor-not-allowed opacity-60' : 'bg-background border-border'
    }`;

  const ToggleRow = ({ icon: IconComponent, label, description, settingKey, value, onChange }) => (
    <div className="bg-muted/40 rounded-lg p-4 border border-border">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="h-8 w-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
            <IconComponent className="w-4 h-4 text-primary" />
          </div>
          <div>
            <p className="font-medium text-foreground text-sm">{label}</p>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
        </div>
        <button
          onClick={() => onChange(settingKey, !value)}
          className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer flex-shrink-0"
          style={{ backgroundColor: value ? 'var(--color-primary)' : 'var(--color-muted)' }}
          aria-label={label}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${value ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>
    </div>
  );

  const timeOptions = [
    { value: '05:00', label: '5:00 AM' },
    { value: '06:00', label: '6:00 AM' },
    { value: '07:00', label: '7:00 AM' },
    { value: '08:00', label: '8:00 AM' },
    { value: '09:00', label: '9:00 AM' },
    { value: '10:00', label: '10:00 AM' },
    { value: '12:00', label: '12:00 PM' },
    { value: '15:00', label: '3:00 PM' },
    { value: '18:00', label: '6:00 PM' },
    { value: '19:00', label: '7:00 PM' },
    { value: '20:00', label: '8:00 PM' },
    { value: '21:00', label: '9:00 PM' },
  ];

  const frequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekdays', label: 'Weekdays Only' },
    { value: 'weekends', label: 'Weekends Only' },
    { value: 'weekly', label: 'Weekly' },
  ];

  return (
    <div className="space-y-6">
      {/* Daily Reminder Settings */}
      <div className="bg-card rounded-2xl border border-border shadow-warm-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
            <Bell className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-bold text-foreground">Notification Preferences</h2>
            <p className="text-muted-foreground text-sm">Control when and how you receive reminders</p>
          </div>
        </div>

        <div className="space-y-3">
          <ToggleRow
            icon={Bell}
            label="Daily Study Reminders"
            description="Receive a daily reminder to complete your review and reading"
            settingKey="dailyReminders"
            value={notifications?.dailyReminders ?? true}
            onChange={handleNotificationChange}
          />
          <ToggleRow
            icon={Mail}
            label="Reading Plan Emails"
            description="Opt-in to receive email updates about your reading plan progress"
            settingKey="planEmails"
            value={notifications?.planEmails ?? true}
            onChange={handleNotificationChange}
          />
          <ToggleRow
            icon={BookOpen}
            label="Bible Reading Reminders"
            description="Reminders specific to your Bible reading plan schedule"
            settingKey="bibleReadingReminders"
            value={notifications?.bibleReadingReminders ?? true}
            onChange={handleNotificationChange}
          />
          <ToggleRow
            icon={BookMarked}
            label="EGW Reading Reminders"
            description="Reminders for your Ellen G. White reading plan"
            settingKey="egwReadingReminders"
            value={notifications?.egwReadingReminders ?? false}
            onChange={handleNotificationChange}
          />
          <ToggleRow
            icon={Calendar}
            label="Study Plan Updates"
            description="Notifications about reading plan milestones and completions"
            settingKey="studyUpdates"
            value={notifications?.studyUpdates ?? false}
            onChange={handleNotificationChange}
          />
        </div>
      </div>

      {/* Reminder Time & Frequency */}
      <div className="bg-card rounded-2xl border border-border shadow-warm-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
            <Clock className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-bold text-foreground">Reminder Schedule</h2>
            <p className="text-muted-foreground text-sm">Set the time and frequency for your daily reminders</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              <Clock className="w-4 h-4 inline mr-1.5 text-primary" />
              Daily Reminder Time
            </label>
            <select
              value={reminderSettings?.reminderTime || '08:00'}
              onChange={(e) => handleReminderChange('reminderTime', e?.target?.value)}
              disabled={!isEditing || !notifications?.dailyReminders}
              className={inputClass(!isEditing || !notifications?.dailyReminders)}
            >
              {timeOptions?.map(opt => (
                <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1">Time is based on your profile timezone</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              <Repeat className="w-4 h-4 inline mr-1.5 text-primary" />
              Reminder Frequency
            </label>
            <select
              value={reminderSettings?.reminderFrequency || 'daily'}
              onChange={(e) => handleReminderChange('reminderFrequency', e?.target?.value)}
              disabled={!isEditing || !notifications?.dailyReminders}
              className={inputClass(!isEditing || !notifications?.dailyReminders)}
            >
              {frequencyOptions?.map(opt => (
                <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1">How often to send reminders</p>
          </div>
        </div>

        {!notifications?.dailyReminders && (
          <div className="mt-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
            <p className="text-sm text-warning">Daily reminders are currently disabled. Enable them above to configure the schedule.</p>
          </div>
        )}
      </div>

      {/* Content Preferences */}
      <div className="bg-card rounded-2xl border border-border shadow-warm-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-bold text-foreground">Content Preferences</h2>
            <p className="text-muted-foreground text-sm">Choose which content types to include in your reminders</p>
          </div>
        </div>

        <div className="space-y-3">
          <ToggleRow
            icon={BookOpen}
            label="Include Bible Verse in Reminders"
            description="Show a daily Bible verse in your reminder notifications"
            settingKey="includeVerseInReminder"
            value={reminderSettings?.includeVerseInReminder ?? true}
            onChange={handleReminderChange}
          />
          <ToggleRow
            icon={BookMarked}
            label="Include EGW Quote in Reminders"
            description="Show an Ellen G. White quote in your reminder notifications"
            settingKey="includeEgwInReminder"
            value={reminderSettings?.includeEgwInReminder ?? false}
            onChange={handleReminderChange}
          />
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Preferred Bible Version</label>
            <select
              value={reminderSettings?.preferredBibleVersion || 'KJV'}
              onChange={(e) => handleReminderChange('preferredBibleVersion', e?.target?.value)}
              disabled={!isEditing}
              className={inputClass(!isEditing)}
            >
              <option value="KJV">King James Version (KJV)</option>
              <option value="ASV">American Standard Version (ASV)</option>
              <option value="NKJV">New King James Version (NKJV)</option>
              <option value="NIV">New International Version (NIV)</option>
              <option value="ESV">English Standard Version (ESV)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">EGW Content Preference</label>
            <select
              value={reminderSettings?.egwContentPreference || 'devotional'}
              onChange={(e) => handleReminderChange('egwContentPreference', e?.target?.value)}
              disabled={!isEditing || !reminderSettings?.includeEgwInReminder}
              className={inputClass(!isEditing || !reminderSettings?.includeEgwInReminder)}
            >
              <option value="devotional">Devotional Writings</option>
              <option value="steps-to-christ">Steps to Christ</option>
              <option value="desire-of-ages">Desire of Ages</option>
              <option value="great-controversy">Great Controversy</option>
              <option value="any">Any EGW Writing</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
